<script setup lang="ts">
import SchemaControls from './ConfigControls.vue'
import IconButton from '@/components/IconButton.vue'
import { Input } from '@/components/ui/input'
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent
} from '@/components/ui/accordion'
import { Plus, Trash2 } from 'lucide-vue-next'
import type { CoordinateTuple } from '@webgamekit/threejs'
import { logicClassifyPathSegment, type PathStepType } from '@webgamekit/logic'
import type { PathEntry, PathConfig } from '@/stores/debugScene'
import { useDebugSceneStore } from '@/stores/debugScene'

/** Tooltip copy for each step type shown beside a stepped path's waypoints. */
const STEP_DESCRIPTIONS: Record<PathStepType, string> = {
  walk: 'Walk — straight horizontal move. A descent walks off the edge and falls via gravity.',
  'forward-jump': 'Forward jump — a parabola up and over, landing on a higher ledge.',
  jump: 'Jump — hops straight up and back down in place (e.g. to bump a block above).'
}

/**
 * Step type of the segment from waypoint `index` to the next (wrapping at the end).
 * @param path The path the waypoint belongs to.
 * @param index The waypoint's position in the path.
 * @returns The step type of that segment.
 */
const stepTypeAt = (path: PathEntry, index: number): PathStepType =>
  logicClassifyPathSegment(
    path.waypoints[index],
    path.waypoints[(index + 1) % path.waypoints.length]
  )

interface Properties {
  path: PathEntry
}

const props = defineProps<Properties>()

const debugSceneStore = useDebugSceneStore()

/**
 * True for the waypoint row currently being interacted with in the viewport.
 * @param index The waypoint's position in the path.
 * @returns True for the active waypoint of this path.
 */
const isActiveRow = (index: number): boolean => {
  const active = debugSceneStore.activeWaypoint
  return active?.pathId === props.path.id && active.index === index
}

const handleUpdateWaypoint = (index: number, axis: 0 | 1 | 2, rawValue: string | number) => {
  const parsed = Number(rawValue)
  if (Number.isNaN(parsed)) return
  const current = props.path.waypoints[index]
  const next: CoordinateTuple = [current[0], current[1], current[2]]
  next[axis] = parsed
  debugSceneStore.updatePathWaypoint(props.path.id, index, next)
}

const handleDeleteWaypoint = (index: number) => {
  debugSceneStore.removePathWaypoint(props.path.id, index)
}

const handleAddWaypoint = () => {
  const last = props.path.waypoints.at(-1) ?? [0, 0, 0]
  debugSceneStore.addPathWaypoint(props.path.id, [last[0], last[1], last[2]])
}

// Playing pauses only this element's path, independent of the global timeline.
const handleConfigUpdate = (key: string, value: unknown) => {
  debugSceneStore.updatePathConfig(props.path.id, key as keyof PathConfig, value)
}

const pathSchema = {
  speed: { min: 1, max: 100, step: 1, label: 'Speed' },
  curved: { boolean: true, label: 'Curved' },
  easing: { bezier: true, label: 'Easing' },
  easingIntensity: { min: 0, max: 5, step: 0.01, label: 'Easing Intensity' },
  playing: { boolean: true, label: 'Playing' },
  loop: { boolean: true, label: 'Loop' },
  pingPong: { boolean: true, label: 'Ping Pong' },
  showPath: { boolean: true, label: 'Show Path' },
  showNodes: { boolean: true, label: 'Show Nodes' },
  reset: { callback: 'reset', label: 'Reset Path' }
}
</script>

<template>
  <!-- Collapsed to start: a path's waypoint table and its dozen settings are longer than
       everything else on an element put together, and push whatever sits above them off
       the panel. -->
  <Accordion type="multiple" class="element-path-section">
    <AccordionItem value="path">
      <AccordionTrigger>Path ({{ path.waypoints.length }})</AccordionTrigger>
      <AccordionContent>
        <div class="element-path-section__content">
          <div class="element-path-section__actions">
            <IconButton panel-colors size="xs" title="Add waypoint" @click="handleAddWaypoint">
              <Plus />
            </IconButton>
            <IconButton
              panel-colors
              size="xs"
              title="Remove path"
              @click="debugSceneStore.removePath(path.id)"
            >
              <Trash2 />
            </IconButton>
          </div>
          <div
            class="element-path-section__grid"
            :class="{ 'element-path-section__grid--stepped': path.stepped }"
          >
            <div class="element-path-section__head">
              <span></span>
              <span v-if="path.stepped"></span>
              <span class="element-path-section__axis-head">x</span>
              <span class="element-path-section__axis-head">y</span>
              <span class="element-path-section__axis-head">z</span>
              <span></span>
            </div>
            <div
              v-for="(position, index) in path.waypoints"
              :key="index"
              class="element-path-section__row"
              :class="{ 'element-path-section__row--active': isActiveRow(index) }"
            >
              <span class="element-path-section__index">{{ index + 1 }}</span>
              <span
                v-if="path.stepped"
                class="element-path-section__step"
                :title="STEP_DESCRIPTIONS[stepTypeAt(path, index)]"
              >
                {{ stepTypeAt(path, index) }}
              </span>
              <Input
                v-for="(axis, axisIndex) in ['x', 'y', 'z'] as const"
                :key="axis"
                class="element-path-section__input"
                type="number"
                :model-value="position[axisIndex as 0 | 1 | 2]"
                @update:model-value="handleUpdateWaypoint(index, axisIndex as 0 | 1 | 2, $event)"
              />
              <IconButton
                panel-colors
                size="xs"
                title="Remove waypoint"
                @click="handleDeleteWaypoint(index)"
              >
                <Trash2 />
              </IconButton>
            </div>
          </div>

          <SchemaControls
            :schema="pathSchema"
            :get-value="(key: string) => (path.config as Record<string, unknown>)[key]"
            :on-update="handleConfigUpdate"
            :on-action="
              (name: string) => {
                if (name === 'reset') path.handlers.onReset()
              }
            "
          />
        </div>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
</template>

<style scoped>
.element-path-section {
  margin-top: var(--spacing-2);
}

.element-path-section__content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
}

.element-path-section__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-1);
}

.element-path-section__grid {
  display: grid;
  grid-template-columns: auto 1fr 1fr 1fr auto;
  align-items: center;
  gap: var(--spacing-0-5);
}

.element-path-section__grid--stepped {
  grid-template-columns: auto auto 1fr 1fr 1fr auto;
}

.element-path-section__head,
.element-path-section__row {
  display: contents;
}

/* Row is display:contents, so tint each of its cells to highlight the active node. */
.element-path-section__row--active > * {
  background-color: var(--panel-item-bg-hover);
}

.element-path-section__axis-head {
  font-size: var(--font-size-xs);
  color: var(--color-muted-foreground);
  font-family: monospace;
  text-align: center;
}

.element-path-section__step {
  font-size: var(--font-size-xs);
  font-weight: 500;
  color: var(--color-primary);
  text-transform: capitalize;
  white-space: nowrap;
  cursor: help;
}

.element-path-section__index {
  font-size: var(--font-size-xs);
  color: var(--color-muted-foreground);
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.element-path-section__input {
  min-width: 0;
  height: var(--btn-xs-height);
  padding: 0 var(--spacing-1);
  font-size: var(--font-size-xs);
  font-variant-numeric: tabular-nums;
  text-align: right;
}
</style>
